import { lazy, Suspense, useEffect } from 'react';
import Raven from 'raven-js';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { omit } from '../../utils/props';
import { isInAppPurchaseMotion } from '../../utils/purchaseMotionTypeGuards';
import { ErrorBoundary, ErrorBoundaryNames } from '../ErrorBoundary';
import { OptionalDFCProvider } from './OptionalDFCProvider';
import { jsx as _jsx } from "react/jsx-runtime";
const AssignSeatButton = /*#__PURE__*/lazy(() => import('../PurchaseMotions/AssignSeatButton/AssignSeatButton').then(m => ({
  default: m.AssignSeatButton
})));
const BuyingButton = /*#__PURE__*/lazy(() => import('../PurchaseMotions/BuyingButton/BuyingButton').then(m => ({
  default: m.BuyingButton
})));
const PricingPageRedirectButton = /*#__PURE__*/lazy(() => import('../PurchaseMotions/PricingPageRedirectButton/PricingPageRedirectButton').then(m => ({
  default: m.PricingPageRedirectButton
})));
const RequestProductButton = /*#__PURE__*/lazy(() => import('../PurchaseMotions/RequestProductButton/RequestProductButton').then(m => ({
  default: m.RequestProductButton
})));
const RequestSeatButton = /*#__PURE__*/lazy(() => import('../PurchaseMotions/RequestSeatButton/RequestSeatButton').then(m => ({
  default: m.RequestSeatButton
})));
const TalkToSalesButton = /*#__PURE__*/lazy(() => import('../PurchaseMotions/TalkToSalesButton/TalkToSalesButton').then(m => ({
  default: m.TalkToSalesButton
})));
const TrialButton = /*#__PURE__*/lazy(() => import('../PurchaseMotions/TrialButton/TrialButton').then(m => ({
  default: m.TrialButton
})));
const ViewAccountButton = /*#__PURE__*/lazy(() => import('../PurchaseMotions/ViewAccountButton/ViewAccountButton').then(m => ({
  default: m.ViewAccountButton
})));
export const PurchaseMotionCTABase = ({
  apiName,
  ctaProps,
  purchaseMotion,
  uiButtonProps = {},
  trackingData,
  onClick,
  upgradeConfig,
  children,
  'data-test-id': dataTestId,
  'data-selenium-id': dataSeleniumId,
  isUpgradePoint
}) => {
  //default to primary if not specified
  uiButtonProps = Object.assign({
    use: 'primary'
  }, uiButtonProps);
  const filteredUiButtonProps = omit(uiButtonProps, ['children', 'className', 'external', 'href', 'onClick', 'style']);
  useEffect(() => {
    if (purchaseMotion !== undefined && !isInAppPurchaseMotion(purchaseMotion)) {
      Raven.captureException(new Error(`Unsupported PurchaseMotion: ${readUnhandledEnumCase(purchaseMotion)}`), {
        fingerprint: ['Failed to load PurchaseMotionCTA purchase motion']
      });
    }
  }, [purchaseMotion]);
  switch (purchaseMotion) {
    case 'ASSIGN_SEAT':
      return /*#__PURE__*/_jsx(AssignSeatButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    case 'BUY_ADDITIONAL_CONTACTS':
    case 'BUY_ADDITIONAL_SEATS':
    case 'BUY_TOUCHLESS':
      return /*#__PURE__*/_jsx(BuyingButton, Object.assign({
        purchaseMotion: purchaseMotion,
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'PRICING_PAGE_REDIRECT':
      return /*#__PURE__*/_jsx(PricingPageRedirectButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'REQUEST_SEAT':
      return /*#__PURE__*/_jsx(RequestSeatButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'REQUEST_PRODUCT':
      return /*#__PURE__*/_jsx(RequestProductButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'TALK_TO_SALES':
    case 'TALK_TO_SALES_FALLBACK':
      return /*#__PURE__*/_jsx(TalkToSalesButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'TRIAL':
      return /*#__PURE__*/_jsx(TrialButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'VIEW_ACCOUNT':
      return /*#__PURE__*/_jsx(ViewAccountButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    default:
      return null;
  }
};
export const PurchaseMotionCTA = props => {
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: ErrorBoundaryNames.PURCHASE_MOTION_CTA,
    children: /*#__PURE__*/_jsx(OptionalDFCProvider, {
      children: /*#__PURE__*/_jsx(Suspense, {
        fallback: /*#__PURE__*/_jsx(LoadingButton, Object.assign({
          use: "primary"
        }, props.uiButtonProps, {
          loading: true,
          children: props.children
        })),
        children: /*#__PURE__*/_jsx(PurchaseMotionCTABase, Object.assign({}, props))
      })
    })
  });
};